import * as React from 'react';
import classnames from 'classnames';

import {
    type SelectProps,
    type SelectOptions,
    type IconNames,
    useUniqueId,
    Label,
    Select,
    Icon,
    LoadingIndicator,
    FieldNote,
} from '@petsmart-ui/sparky';
import { isSafari } from '../../utils/browser';

/**
 * This is a extended version on original <SelectField /> component, located in Sparky lib
 * After migration done it could replace corresponding Sparky implementation
 */

export interface SelectFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLSelectElement>, 'size'> {
    /** HTML id for the component */
    id?: SelectProps['id'];
    /** CSS class names that can be appended to the component. */
    className?: SelectProps['className'];
    /** Turns on read only status for select */
    readOnly?: SelectProps['readOnly'];
    /** Indicates that field is required for form to be successfully submitted */
    required?: SelectProps['required'];

    /** The array of items to be passed into the component. The array must take on the specified shape */
    items: SelectOptions;
    /** The HTML value attribute of the select menu */
    selectedValue?: SelectProps['selectedValue'];
    /** A string representing the default value of the selected radio button */
    defaultValue?: SelectProps['defaultValue'];

    /** Size variations for the Select field
     * - **sm** results in a visually smaller select input
     */
    size?: 'sm' | 'md';

    /** HTML id of the helper text used to describe the component */
    ariaDescribedBy?: SelectProps['ariaDescribedBy'];
    /** Disables the field and prevents editing the contents */
    disabled?: SelectProps['disabled'];
    /** Used as helper text or error message */
    fieldNote?: string | React.ReactNode;

    /** Turns on floating label pattern */
    floating?: boolean;
    /** Toggles the visibility of the label. If hidden, the label text will still be accessible to assistive technologies */
    hideLabel?: boolean;
    /** Name of SVG icon for state */
    iconName?: IconNames;
    /** Inverted variant for dark backgrounds */
    inverted?: boolean;
    /** Error state of the form field */
    isError?: boolean;
    /** HTML label text */
    label: string;

    /** String for the optional label. By default it is '(optional)' */
    optionalLabel?: string;

    /** String for the required label to add additional information if needed. */
    requiredLabel?: string;

    firstOption?: SelectProps['firstOption'];

    /** Function that fires when field value has changed */
    onChange?: React.ChangeEventHandler;
    /** Function that fires when field value is being navigated away to */
    onFocus?: React.FocusEventHandler;
    /** Function that fires when field is being navigated away from */
    onBlur?: React.FocusEventHandler;

    isLoading?: boolean;
    showTopLabel?: boolean;
}

/** A control that provides a menu of options. */
export function SelectField({
    className,
    id,
    label,
    disabled,
    required,
    floating = true,
    hideLabel,
    optionalLabel,
    ariaDescribedBy,
    isError,
    inverted,
    fieldNote,
    requiredLabel,
    iconName,
    items,
    readOnly,
    selectedValue,
    defaultValue,
    size,
    firstOption,
    onChange,
    onBlur,
    onFocus,
    isLoading,
    showTopLabel = true,
    ...other
}: SelectFieldProps) {
    const [idVar] = useUniqueId(id);
    const [ariaDescribedByVar, setAriaDescribedBy] = React.useState<string>('');
    const [focusState, setFocusState] = React.useState<boolean>(false);

    const ref = React.useRef<HTMLDivElement>(null);
    const selectRef = React.useRef<HTMLSelectElement | null>(null);

    React.useEffect(() => {
        if (fieldNote) {
            setAriaDescribedBy(ariaDescribedBy || idVar);
        }
    }, [selectedValue, idVar, fieldNote, ariaDescribedBy]);

    /**
     * Handle onChange
     * 1) On change, set value to the event target value
     * 2) If onChange prop exists, run the function passed into it on change
     */
    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
        setFocusState(false);
        onBlur?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
        setFocusState(true);
        onFocus?.(e);
    };

    const hasSelection =
        Boolean(defaultValue) ||
        Boolean(selectRef.current?.value) ||
        Boolean(selectedValue !== '' && selectedValue !== undefined);

    const componentClassName = classnames('sparky-c-select-field', className, {
        'sparky-c-select-field--sm': size === 'sm',
        'sparky-c-select-field--md': size === 'md',
        'sparky-c-select-field--inverted': inverted === true,
        'sparky-c-select-field--floating-label': floating === true,
        'sparky-is-error': isError,
        'sparky-is-disabled': disabled,
        'sparky-is-focused': focusState === true,
        'sparky-is-filled': hasSelection === true,
    });

    const renderFirstOption =
        firstOption && !floating ? (
            firstOption
        ) : floating && size !== 'sm' && !isSafari() ? (
            <option value="" hidden />
        ) : undefined;

    return (
        <div className={componentClassName} ref={ref}>
            {!floating && showTopLabel ? (
                <Label
                    className="sparky-c-select-field__label"
                    htmlFor={idVar}
                    text={label}
                    hideLabel={hideLabel}
                    required={required}
                    inverted={inverted}
                    optionalLabel={optionalLabel}
                    requiredLabel={requiredLabel}
                />
            ) : undefined}

            <div className="sparky-c-select-field__body">
                <Select
                    id={idVar}
                    ref={selectRef}
                    className="sparky-c-select-field__input"
                    items={items}
                    readOnly={readOnly}
                    disabled={disabled}
                    ariaDescribedBy={fieldNote ? ariaDescribedByVar : undefined}
                    selectedValue={selectedValue}
                    defaultValue={defaultValue}
                    required={required}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={onChange}
                    float={floating}
                    firstOption={renderFirstOption}
                    {...other}
                />
                {floating ? (
                    <Label
                        className="sparky-c-select-field__label"
                        htmlFor={idVar}
                        text={label}
                        hideLabel={hideLabel || size === 'sm'}
                        required={required}
                        inverted={inverted}
                        optionalLabel={optionalLabel}
                        requiredLabel={requiredLabel}
                    />
                ) : undefined}

                <div className="sparky-c-select-field__input-within">
                    {iconName && (
                        <Icon
                            className="sparky-c-select-field__status-icon"
                            name={iconName}
                            size="lg"
                        />
                    )}
                    {isLoading ? (
                        <LoadingIndicator />
                    ) : (
                        <Icon
                            name="chevron-down"
                            className="sparky-c-select-field__icon"
                            size="md"
                        />
                    )}
                </div>
            </div>

            {fieldNote && (
                <FieldNote
                    className="sparky-c-select-field__note"
                    id={ariaDescribedByVar}
                    isError={isError}
                    inverted={inverted}
                >
                    {fieldNote}
                </FieldNote>
            )}
        </div>
    );
}
